<!-- src/lib/components/MilestonesView.svelte -->
<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';

  interface Milestone {
    id: string;
    project_id: string;
    title: string;
    description: string | null;
    due_date: string | null;
    status: string;
    created_at: string;
    updated_at: string;
  }

  let milestones = $state<Milestone[]>([]);
  let showNew = $state(false);
  let newTitle = $state('');
  let newDue = $state('');
  let creating = $state(false);

  $effect(() => {
    const projectId = pmStore.activeProjectId;
    if (!projectId) return;
    api<Milestone[]>(`/api/v1/projects/${projectId}/milestones`)
      .then(m => { milestones = m; })
      .catch(() => {});
  });

  async function createMilestone() {
    const projectId = pmStore.activeProjectId;
    if (!newTitle.trim() || !projectId) return;
    creating = true;
    try {
      const m = await api<Milestone>(`/api/v1/projects/${projectId}/milestones`, {
        method: 'POST',
        body: JSON.stringify({ title: newTitle.trim(), due_date: newDue || null }),
      });
      milestones = [...milestones, m];
      newTitle = ''; newDue = ''; showNew = false;
    } finally { creating = false; }
  }

  async function closeMilestone(id: string) {
    const m = await api<Milestone>(`/api/v1/milestones/${id}/close`, { method: 'POST' });
    milestones = milestones.map(ms => ms.id === id ? m : ms);
  }

  async function deleteMilestone(id: string) {
    await api(`/api/v1/milestones/${id}`, { method: 'DELETE' });
    milestones = milestones.filter(m => m.id !== id);
  }

  function isOverdue(dueDate: string | null): boolean {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  }

  function formatDue(dueDate: string | null): string {
    if (!dueDate) return 'No due date';
    const d = new Date(dueDate);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const openMilestones = $derived(milestones.filter(m => m.status === 'open'));
  const closedMilestones = $derived(milestones.filter(m => m.status === 'closed'));
</script>

<div class="milestones-view">
  <header class="view-header">
    <h2>Milestones</h2>
    <span class="milestone-counts">
      {openMilestones.length} open · {closedMilestones.length} closed
    </span>
    <button class="new-btn" onclick={() => { showNew = true; }}>+ New Milestone</button>
  </header>

  {#if showNew}
    <form class="new-form" onsubmit={(e) => { e.preventDefault(); createMilestone(); }}>
      <input bind:value={newTitle} placeholder="Milestone title" class="milestone-input" />
      <input type="date" bind:value={newDue} class="milestone-date-input" />
      <button type="submit" disabled={creating}>Create</button>
      <button type="button" onclick={() => { showNew = false; }}>Cancel</button>
    </form>
  {/if}

  <div class="milestone-list">
    {#each openMilestones as milestone}
      <div class="milestone-item" class:overdue={isOverdue(milestone.due_date)}>
        <div class="milestone-icon">◎</div>
        <div class="milestone-content">
          <div class="milestone-title">{milestone.title}</div>
          <div class="milestone-meta">
            <span class="due-date" class:overdue-text={isOverdue(milestone.due_date)}>
              {formatDue(milestone.due_date)}
            </span>
          </div>
        </div>
        <div class="milestone-actions">
          <button class="action-btn" onclick={() => closeMilestone(milestone.id)} title="Close milestone">✓</button>
          <button class="action-btn danger" onclick={() => deleteMilestone(milestone.id)} title="Delete">✕</button>
        </div>
      </div>
    {/each}

    {#if closedMilestones.length > 0}
      <div class="section-divider">Closed</div>
      {#each closedMilestones as milestone}
        <div class="milestone-item closed">
          <div class="milestone-icon">✓</div>
          <div class="milestone-content">
            <div class="milestone-title">{milestone.title}</div>
            <div class="milestone-meta">{formatDue(milestone.due_date)}</div>
          </div>
          <div class="milestone-actions">
            <button class="action-btn danger" onclick={() => deleteMilestone(milestone.id)}>✕</button>
          </div>
        </div>
      {/each}
    {/if}

    {#if milestones.length === 0}
      <div class="empty-state">
        <p>No milestones yet.</p>
        <p class="empty-sub">Track major project checkpoints and release goals.</p>
      </div>
    {/if}
  </div>
</div>

<style>
  .milestones-view {
    padding: 1.5rem;
    max-width: 720px;
    overflow-y: auto;
    flex: 1;
  }

  .view-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  h2 {
    font-size: 1.1rem;
    color: var(--text-primary);
    margin: 0;
  }

  .milestone-counts {
    font-size: 0.75rem;
    color: var(--text-muted, rgba(255,255,255,0.4));
  }

  .new-btn {
    margin-left: auto;
    background: oklch(0.72 0.28 310 / 0.12);
    border: 1px solid oklch(0.72 0.28 310 / 0.32);
    color: oklch(0.80 0.26 310);
    padding: 0.35rem 0.75rem;
    border-radius: 5px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: background 80ms;
  }

  .new-btn:hover {
    background: oklch(0.72 0.28 310 / 0.22);
  }

  .new-form {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
    align-items: center;
  }

  .milestone-input {
    flex: 1;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.12);
    color: var(--text-primary);
    padding: 0.4rem 0.6rem;
    border-radius: 5px;
    font-size: 0.85rem;
    outline: none;
  }

  .milestone-input:focus {
    border-color: oklch(0.74 0.28 310 / 0.46);
  }

  .milestone-date-input {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.12);
    color: var(--text-primary);
    padding: 0.4rem 0.6rem;
    border-radius: 5px;
    font-size: 0.85rem;
    outline: none;
  }

  .new-form button[type="submit"] {
    background: oklch(0.72 0.28 310 / 0.12);
    border: 1px solid oklch(0.72 0.28 310 / 0.32);
    color: oklch(0.80 0.26 310);
    padding: 0.4rem 0.75rem;
    border-radius: 5px;
    font-size: 0.8rem;
    cursor: pointer;
  }

  .new-form button[type="submit"]:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .new-form button[type="button"] {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.4);
    padding: 0.4rem 0.75rem;
    border-radius: 5px;
    font-size: 0.8rem;
    cursor: pointer;
  }

  .milestone-list {
    display: flex;
    flex-direction: column;
  }

  .milestone-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-faint);
    transition: background 80ms;
  }

  .milestone-item:hover {
    background: rgba(255,255,255,0.03);
  }

  .milestone-item.overdue {
    background: oklch(0.62 0.26 25 / 0.04);
  }

  .milestone-item.closed {
    opacity: 0.5;
  }

  .milestone-icon {
    font-size: 1rem;
    color: oklch(0.80 0.26 310 / 0.68);
    width: 1.5rem;
    text-align: center;
    flex-shrink: 0;
  }

  .milestone-item.closed .milestone-icon {
    color: rgba(255,255,255,0.2);
  }

  .milestone-content {
    flex: 1;
  }

  .milestone-title {
    font-size: 0.875rem;
    color: var(--text-primary);
  }

  .milestone-meta {
    font-size: 0.72rem;
    color: var(--text-muted);
    margin-top: 0.125rem;
  }

  .overdue-text {
    color: #ff5555;
  }

  .milestone-actions {
    display: flex;
    gap: 0.25rem;
    opacity: 0;
    transition: opacity 80ms;
  }

  .milestone-item:hover .milestone-actions {
    opacity: 1;
  }

  .action-btn {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.4);
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.75rem;
    transition: background 80ms, border-color 80ms, color 80ms;
  }

  .action-btn:hover {
    background: rgba(255,255,255,0.06);
  }

  .action-btn.danger:hover {
    background: oklch(0.62 0.26 25 / 0.10);
    border-color: oklch(0.62 0.26 25 / 0.30);
    color: #ff5555;
  }

  .section-divider {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(255,255,255,0.2);
    padding: 0.75rem 1rem 0.25rem;
  }

  .empty-state {
    text-align: center;
    padding: 3rem;
    color: rgba(255,255,255,0.25);
    font-size: 0.85rem;
  }

  .empty-sub {
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
</style>
